<script lang="ts">
  /**
   * Top 10 colaboradores por excesso de jornada (horizontal bars).
   * Mostra o `rawAnchor` (CPF/PIS/matrícula) — o app roda local-first; rawAnchor
   * é seguro pra apresentação ao consultor mas NUNCA aparece em export PDF/HTML.
   */
  import type { RankedMetric } from '$core/metrics/metrics.types';
  import type { CanonicalEmployee } from '$core/domain/canonical.types';
  import { formatMinutesCompact } from './chartUtils';

  export let ranked: RankedMetric;
  export let employees: CanonicalEmployee[] = [];

  $: items = ranked.items.slice(0, 10);
  $: maxValue = items[0]?.value ?? 1;

  $: anchorById = (() => {
    const map = new Map<string, { anchor?: string; kind?: string }>();
    for (const e of employees) {
      map.set(e.identity.id, {
        anchor: e.identity.rawAnchor,
        kind: e.identity.anchor,
      });
    }
    return map;
  })();

  /** Mascara CPF (3 primeiros + 2 últimos) ou PIS (5 primeiros). */
  function maskAnchor(raw: string, kind: string | undefined): string {
    const digits = raw.replace(/\D/g, '');
    if (kind === 'cpf' && digits.length === 11) {
      return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
    }
    if (kind === 'pis' && digits.length === 11) {
      return `${digits.slice(0, 3)}.${digits.slice(3, 8)}.**-*`;
    }
    if (digits.length >= 6) {
      return `${digits.slice(0, 3)}.***.${digits.slice(-2)}`;
    }
    return raw;
  }

  function labelFor(employeeId: string, idx: number): { line1: string; line2: string } {
    const meta = anchorById.get(employeeId);
    if (meta?.anchor) {
      const kindLabel = meta.kind === 'cpf' ? 'CPF' : meta.kind === 'pis' ? 'PIS' : 'Matrícula';
      return { line1: `#${idx + 1}`, line2: `${kindLabel} ${maskAnchor(meta.anchor, meta.kind)}` };
    }
    const short = employeeId.length > 8 ? employeeId.slice(0, 8) + '…' : employeeId;
    return { line1: `#${idx + 1}`, line2: short };
  }
</script>

<div class="chart">
  <header class="chart__head">
    <h3 class="chart__title">Top colaboradores em hora extra</h3>
    <p class="chart__subtitle">Maiores volumes de minutos acima da jornada (visível só localmente)</p>
  </header>

  {#if items.length === 0}
    <p class="empty">Nenhum excesso de jornada identificado no período.</p>
  {:else}
    <ol class="bars">
      {#each items as item, i}
        {@const ratio = Math.max(0.02, item.value / Math.max(1, maxValue))}
        {@const label = labelFor(String(item.key), i)}
        <li class="bar">
          <span class="bar__label">
            <span class="bar__rank">{label.line1}</span>
            <span class="bar__id">{label.line2}</span>
          </span>
          <div class="bar__track">
            <div
              class="bar__fill"
              style="width: {(ratio * 100).toFixed(1)}%;"
            ></div>
          </div>
          <span class="bar__value">{formatMinutesCompact(item.value)}</span>
        </li>
      {/each}
    </ol>
  {/if}
</div>

<style>
  .chart { display: flex; flex-direction: column; gap: var(--ptt-space-3); }
  .chart__title { margin: 0; font-size: 1rem; font-weight: 700; color: var(--ptt-gray-300); }
  .chart__subtitle { margin: 2px 0 0; font-size: 0.8125rem; color: var(--ptt-gray-100); }
  .empty { font-size: 0.875rem; color: var(--ptt-gray-100); }
  .bars { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--ptt-space-2); }
  .bar {
    display: grid;
    grid-template-columns: 160px 1fr 90px;
    align-items: center;
    gap: var(--ptt-space-3);
    font-size: 0.8125rem;
  }
  .bar__label {
    display: flex;
    align-items: baseline;
    gap: var(--ptt-space-2);
    color: var(--ptt-gray-200);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .bar__rank {
    font-weight: 700;
    color: var(--ptt-gray-300);
    font-variant-numeric: tabular-nums;
  }
  .bar__id {
    font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
    font-size: 0.75rem;
    color: var(--ptt-gray-100);
  }
  .bar__track {
    width: 100%;
    background: var(--ptt-gray-10);
    border-radius: var(--ptt-radius-sm);
    height: 20px;
    overflow: hidden;
  }
  .bar__fill {
    height: 100%;
    border-radius: var(--ptt-radius-sm);
    background: linear-gradient(90deg, #FB7224 0%, #7F3D7D 100%);
  }
  .bar__value {
    font-weight: 600;
    color: var(--ptt-gray-300);
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  @media (max-width: 640px) {
    .bar { grid-template-columns: 130px 1fr 75px; gap: var(--ptt-space-2); font-size: 0.75rem; }
    .bar__id { font-size: 0.6875rem; }
  }
</style>
